import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";

import NavBar from "../components/common/navBar";
import Footer from "../components/common/footer";
import Logo from "../components/common/logo";

import INFO from "../data/user";
import SEO from "../data/seo";

import "./styles/editor.css";

const isYouTubeUrl = (url) => /(youtube\.com|youtu\.be)/i.test(url);

function extractYouTubeId(url) {
  // Handles: watch?v=ID, embed/ID, youtu.be/ID, shorts/ID
  const patterns = [
    /v=([a-zA-Z0-9_-]{11})/, // watch?v=
    /embed\/([a-zA-Z0-9_-]{11})/,
    /youtu\.be\/([a-zA-Z0-9_-]{11})/,
    /shorts\/([a-zA-Z0-9_-]{11})/,
  ];
  for (const re of patterns) {
    const m = url.match(re);
    if (m) return m[1];
  }
  return null;
}

function buildYouTubeEmbedSrc(rawUrl, { autoplay = 1 } = {}) {
  const id = extractYouTubeId(rawUrl);
  const base = id ? `https://www.youtube.com/embed/${id}` : rawUrl; // fallback if parsing fails
  const params = new URLSearchParams({
    autoplay: String(autoplay),
    rel: "0",
    modestbranding: "1",
    playsinline: "1",
  });
  return `${base}?${params.toString()}`;
}

const Editor = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const currentSEO = useMemo(() => SEO.find((item) => item.page === "editor"), []);

  const [playingIndex, setPlayingIndex] = useState(null);

  const videos = INFO.editor;

  return (
    <React.Fragment>
      <Helmet>
        <title>{`Editor | ${INFO.main.title}`}</title>
        {currentSEO?.description && (
          <meta name="description" content={currentSEO.description} />
        )}
        {currentSEO?.keywords && (
          <meta name="keywords" content={currentSEO.keywords.join(", ")} />
        )}
      </Helmet>

      <div className="page-content">
        <NavBar active="editor" />
        <div className="content-wrapper">
          <div className="editor-logo-container">
            <div className="editor-logo">
              <Logo width={46} />
            </div>
          </div>

          <div className="editor-container">
            <div className="title editor-title">Editor</div>
            

            <section className="editor-videos" aria-label="Video gallery">
              {videos.map((v, idx) => {
                const isPlaying = playingIndex === idx;
                const isYT = isYouTubeUrl(v.src);
                return (
                  <article className="editor-video-card" key={idx}>
                    {!isPlaying ? (
                      <button
                        type="button"
                        className="editor-video-poster"
                        onClick={() => setPlayingIndex(idx)}
                        aria-label={`Play ${v.title}`}
                      >
                        {/* Poster image */}
                        <img
                          src={v.thumbnail}
                          alt={v.title}
                          className="editor-video-thumb"
                          loading="lazy"
                        />
                        {/* Blur & tint overlay */}
                        <span className="editor-video-overlay" aria-hidden="true" />

                        {/* Title chip */}
                        <span className="editor-video-title">{v.title}</span>

                        {/* Play button */}
                        <span className="editor-video-play" aria-hidden>
                          <svg
                            width="64"
                            height="64"
                            viewBox="0 0 64 64"
                            xmlns="http://www.w3.org/2000/svg"
                            className="editor-video-play-icon"
                          >
                            <circle cx="32" cy="32" r="30" fill="url(#g)" />
                            <defs>
                              <radialGradient id="g" cx="50%" cy="50%" r="50%">
                                <stop offset="0%" stopColor="var(--link-color, #5cf)" stopOpacity="1" />
                                <stop offset="100%" stopColor="var(--link-color, #5cf)" stopOpacity="0.15" />
                              </radialGradient>
                            </defs>
                            <polygon points="26,20 26,44 46,32" fill="white" />
                          </svg>
                        </span>
                      </button>
                    ) : (
                      <div className="editor-video-player">
                        {isYT ? (
                          <iframe
                            title={v.title}
                            src={buildYouTubeEmbedSrc(v.src, { autoplay: 1 })}
                            allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
                            allowFullScreen
                            loading="lazy"
                          />
                        ) : (
                          <video
                            src={v.src}
                            controls
                            autoPlay
                            playsInline
                            preload="metadata"
                          />
                        )}
                        <button
                          type="button"
                          className="editor-video-exit"
                          onClick={() => setPlayingIndex(null)}
                          aria-label="Stop and show thumbnail"
                          title="Close player"
                        >
                          ×
                        </button>
                      </div>
                    )}
                  </article>
                );
              })}
            </section>
          </div>

          <div className="page-footer">
            <Footer />
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default Editor;